import { useBreakpoints } from "@sazka/utils/composables/use-breakpoints";
import { DataLayer } from "@sazka/utils/data-layer";
import { ResponsiveUtils } from "@sazka/utils/responsive-utils";
import { Analytics } from "@/shared/utils/analytics/analytics";
import { GamesFilterOptions } from "../models/games-filter-options";
import { GamesFilterType } from "../models/games-filter-type";

export namespace GamesAnalytics {
    const lastPushedIndexes = new Map();

    // DL1.10
    export const pushGamesPage = () => {
        DataLayer.push({
            page: {
                type: "lobby.hry",
                kind: "lobby.hry",
                gameCode: "no_game",
                gameCodeStrict: "no_game",
                verticalCode: "hry",
                verticalCodeStrict: "hry",
                responseDesignCategory: ResponsiveUtils.getDataLayerResolution(),
            },
        });

        Analytics.pageReady();
    };

    // DL1.20
    export const pushSearch = (searchText: string, resultsCount: number) => {
        DataLayer.push({
            page: {
                type: "list.searchresults",
                kind: "searchresults",
                responseDesignCategory: ResponsiveUtils.getDataLayerResolution(),
                list: {
                    searchresults: {
                        type: "hry search",
                        term: searchText,
                        resultCount: resultsCount,
                    },
                    pageNumber: 1,
                },
            },
            event: "page",
        });
    };

    // DL2.50
    export const pushMoreQuestsClick = () => {
        DataLayer.push({
            event: "action.openOtherQuests.hry",
        });
    };

    // DL2.90
    export const pushOfficeNewsClick = () => {
        DataLayer.push({
            event: "action.openAllNews.hry",
        });
    };

    const getAnalyticsFilterName = (type: GamesFilterType) => {
        switch (type) {
            case GamesFilterType.Genres:
                return "genre";
            case GamesFilterType.Manufacturers:
                return "supplier";
            case GamesFilterType.Reels:
                return "numberOfCylinders";
            case GamesFilterType.Lines:
                return "numberOfLines";
        }
    };

    export const getFilterOptionNames = (
        filteredGenres: string[],
        filteredManufacturers: string[],
        filteredReels: string[],
        filteredLines: string[],
        filterOptions: GamesFilterOptions
    ) => {
        const genresNames = filteredGenres.map((p) => filterOptions.genres?.find((o) => o.guid === p)?.name);
        const manufacturersNames = filteredManufacturers.map(
            (p) => filterOptions.manufacturers.find((o) => o.guid === p)?.name
        );
        const reelsNames = filteredReels.map((p) => filterOptions.reels?.find((o) => o.guid === p)?.name);
        const linesNames = filteredLines.map((p) => filterOptions.lines?.find((o) => o.guid === p)?.name);
        return genresNames.concat(manufacturersNames, reelsNames, linesNames);
    };

    // DL3.10
    export const pushDesktopFilterOpen = (type: GamesFilterType) => {
        DataLayer.push({
            event: "action.openFilters.hry",
            action: {
                openFilters: {
                    eventProperty1: getAnalyticsFilterName(type),
                },
            },
        });
    };

    // DL3.15
    export const pushFilterApplied = (type: GamesFilterType, appliedFilter: string, allFilters: string[]) => {
        DataLayer.push({
            page: {
                filters: null,
            },
        });

        DataLayer.push({
            event: "action.applyFilter.hry",
            action: {
                applyFilter: {
                    label: appliedFilter,
                    eventProperty1: getAnalyticsFilterName(type),
                },
            },
            page: {
                filters: allFilters,
            },
        });
    };

    // DL3.20
    export const pushFilterRemoved = (type: GamesFilterType, removedFilters: string[], allFilters: string[]) => {
        DataLayer.push({
            page: {
                filters: null,
            },
        });

        DataLayer.push({
            event: "action.removeFilter.hry",
            action: {
                removeFilter: {
                    label: removedFilters?.join(","),
                    eventProperty1: getAnalyticsFilterName(type),
                },
            },
            page: {
                filters: allFilters,
            },
        });
    };

    // DL3.25
    export const pushMobileFilterClosed = () => {
        DataLayer.push({
            event: "action.closeFilters.hry",
        });
    };

    // DL3.30
    export const pushSort = (sortLabel: string) => {
        DataLayer.push({
            event: "action.sort.hry",
            action: {
                sort: {
                    label: sortLabel,
                },
            },
        });
    };

    const clearImpressions = () => {
        DataLayer.push({
            action: {
                impress: {
                    promos: null,
                },
            },
        });
    };

    const getBannerPromo = (banner: HTMLElement, index: number) => {
        return {
            id: banner.dataset.aCodename,
            name: banner.dataset.aCodename,
            creative: banner.querySelector(".games-carousel__title")?.textContent,
            position: `top.carousel.${index + 1}`,
        };
    };

    // DL4.10 + DL4.20
    export const initBannersAnalytics = () => {
        const carouselWrapper = document.querySelector(".games-carousel");
        if (!carouselWrapper) {
            return;
        }

        const allBanners = Array.from(carouselWrapper.querySelectorAll<HTMLElement>(".games-carousel__item"));
        if (!allBanners?.length) {
            return;
        }

        clearImpressions();
        DataLayer.push({
            event: "action.impress.promos",
            action: {
                impress: {
                    gameCode: allBanners[0].dataset.aGameCodename,
                    promos: [getBannerPromo(allBanners[0], 0)],
                },
            },
        });

        lastPushedIndexes.set("banners", 0);

        allBanners.forEach((banner, index) => {
            const button = banner.querySelector<HTMLElement>(".games-button");
            button.addEventListener("click", () => {
                DataLayer.pushInstant({
                    event: "action.show.promos",
                    action: {
                        show: {
                            gameCode: banner.dataset.aGameCodename,
                            promos: [getBannerPromo(banner, index)],
                        },
                    },
                });
            });
        });
    };

    // DL4.10
    export const pushBannerChange = () => {
        const carouselWrapper = document.querySelector(".games-carousel");
        const allBanners = Array.from(carouselWrapper.querySelectorAll<HTMLElement>(".games-carousel__item"));

        const visibleBanner = allBanners.find((b) => b.style.visibility === "visible");
        const visibleBannerIndex = allBanners.indexOf(visibleBanner);
        if (visibleBannerIndex > lastPushedIndexes.get("banners")) {
            clearImpressions();
            DataLayer.push({
                event: "action.impress.promos",
                action: {
                    impress: {
                        gameCode: visibleBanner.dataset.aGameCodename,
                        promos: [getBannerPromo(visibleBanner, visibleBannerIndex)],
                    },
                },
            });
            lastPushedIndexes.set("banners", visibleBannerIndex);
        }
    };

    const getBonusPromo = (bonus: HTMLElement, position: string, index: number) => {
        return {
            id: bonus.dataset.aCodename,
            name: "bonusyGifts",
            creative: bonus.querySelector(".games-bonus__title")?.textContent,
            position: `${position}.${index + 1}`,
        };
    };

    //DL 4.30 + DL4.35
    export const initBonusAnalytics = () => {
        const bonusWrapper = document.querySelector("#bonus-banner");
        if (!bonusWrapper) {
            return;
        }

        const bannerWrapper = document.querySelector(".games-banner__grid");
        const position = Array.prototype.indexOf.call(bannerWrapper?.children, bonusWrapper) > 1 ? "right" : "left";

        const allBonuses = Array.from(bonusWrapper.querySelectorAll<HTMLElement>(".games-bonus"));
        if (!allBonuses?.length) {
            return;
        }

        const promos = allBonuses.map((bonus, index) => getBonusPromo(bonus, position, index));

        clearImpressions();
        DataLayer.push({
            event: "action.impress.promos",
            action: {
                impress: {
                    promos: promos,
                },
            },
        });

        allBonuses.forEach((bonus, index) => {
            bonus.addEventListener("click", () => {
                DataLayer.pushInstant({
                    event: "action.show.promos",
                    action: {
                        show: {
                            promos: [getBonusPromo(bonus, position, index)],
                        },
                    },
                });
            });
        });
    };

    // DL4.40
    export const pushShowBonusomat = (tasks: number) => {
        clearImpressions();
        DataLayer.push({
            event: "action.impress.promos",
            action: {
                impress: {
                    promos: [
                        {
                            id: "bonusomat",
                            name: "bonusomat",
                            creative: `${tasks}/5`,
                            position: "middle",
                        },
                    ],
                },
            },
        });
    };

    // DL4.45 + DL2.91
    export const pushClickBonusomat = (tasks: number) => {
        DataLayer.pushInstant({
            event: "action.show.promos",
            action: {
                show: {
                    promos: [
                        {
                            id: "bonusomat",
                            name: "bonusomat",
                            creative: `${tasks}/5`,
                            position: "middle",
                        },
                    ],
                },
            },
        });

        DataLayer.pushInstant({
            event: "action.openBonusomat.hry",
        });
    };

    const getArticlePromo = (bonus: HTMLElement, index: number, isDesktop: boolean) => {
        let position = `carousel.${index + 1}`;
        if (isDesktop) {
            switch (index) {
                case 0:
                    position = "left";
                    break;
                case 1:
                    position = "top.right";
                    break;
                case 2:
                    position = "bottom.right";
                    break;
            }
        }

        return {
            id: bonus.dataset.aCodename,
            name: "kancelar.news",
            creative: bonus.querySelector(".games-news-item__name")?.textContent,
            position: position,
        };
    };

    //DL 4.60 + DL4.65
    export const initCasinoArticlesAnalytics = () => {
        const articlesWrapper = document.querySelector(".games-news");
        if (!articlesWrapper) {
            return;
        }

        const allArticles = Array.from(articlesWrapper.querySelectorAll<HTMLElement>(".games-news-item"));
        if (!allArticles?.length) {
            return;
        }

        const isDesktop = useBreakpoints("sm");
        const promos = allArticles.map((article, index) => getArticlePromo(article, index, isDesktop.value));

        const visibleArticlesCount = isDesktop.value ? 3 : 1;
        clearImpressions();
        DataLayer.push({
            event: "action.impress.promos",
            action: {
                impress: {
                    promos: promos.slice(0, visibleArticlesCount),
                },
            },
        });

        lastPushedIndexes.set("articles", visibleArticlesCount - 1);

        allArticles.forEach((article, index) => {
            article.addEventListener("click", () => {
                DataLayer.pushInstant({
                    event: "action.show.promos",
                    action: {
                        show: {
                            promos: [getArticlePromo(article, index, isDesktop.value)],
                        },
                    },
                });
            });
        });
    };

    // DL4.60
    export const pushArticleChange = (visibleArticleIndex: number) => {
        const articlesWrapper = document.querySelector(".games-news");
        const allArticles = Array.from(articlesWrapper.querySelectorAll<HTMLElement>(".games-news-item"));

        const lastPushedIndex = lastPushedIndexes.get("articles");
        if (visibleArticleIndex > lastPushedIndex) {
            clearImpressions();
            DataLayer.push({
                event: "action.impress.promos",
                action: {
                    impress: {
                        promos: [getArticlePromo(allArticles[visibleArticleIndex], visibleArticleIndex, false)],
                    },
                },
            });
            lastPushedIndexes.set("articles", visibleArticleIndex);
        }
    };
}
